/* eslint-disable max-lines */
import React, { useEffect, useMemo, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@explorer/core';
import {
  FieldText,
  Form,
  FormFieldControl,
  FormSchemaOptions,
  FormSchemaReturn,
  SubmitButton,
} from '@explorer/forms';
import { ControlClientInfoResponse } from '@explorer/open-api-client';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import { Select, Stack } from '@crucible-risk/security-applications';

import { useMutationCreateWizerAccount } from './useMutationCreateWizerAccount';
import { useQueryUser } from './useQueryUser';

const validationSchema = (intl) =>
  yup
    .object({
      email: yup.string().required(
        intl.formatMessage({
          id: 'sat.wizer.email.required',
        }),
      ),
      firstname: yup.string().required(
        intl.formatMessage({
          id: 'sat.wizer.firstname.required',
        }),
      ),
      lastname: yup.string().required(
        intl.formatMessage({
          id: 'sat.wizer.lastname.required',
        }),
      ),
      company: yup.string().required(
        intl.formatMessage({
          id: 'sat.wizer.company.required',
        }),
      ),
    })
    .defined();

type FormValues = yup.InferType<ReturnType<typeof validationSchema>>;

export const WizerCreateAccountModal = ({
  activeClient,
  open = false,
  handleClose,
}: WizerCreateAccountModalProps) => {
  const intl = useIntl();
  const { palette } = useTheme();

  const users = (activeClient?.associated_users ?? []).filter(
    (user) => user.is_active,
  );

  const [email, setEmail] = useState<string>('');

  const { data, refetch } = useQueryUser(email);

  const { mutate } = useMutationCreateWizerAccount({
    client_id: activeClient.client.client_id,
    onSuccess: () => {
      handleClose(false);
      formik.setSubmitting(false);
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });

  const createFormId = 'wizerCreateAccountForm';

  const formik = useFormik<FormValues>({
    validationSchema: validationSchema(intl),
    initialValues: {
      email: '',
      firstname: '',
      lastname: '',
      company: '',
    },
    onSubmit: (values) => {
      mutate({
        client_id: activeClient.client.client_id,
        user_id: data?.results[0]?.user_id || '',
        email: values.email,
        first_name: values.firstname,
        last_name: values.lastname,
        company_name: values.company,
      });
    },
  });

  useEffect(() => {
    if (email) {
      refetch();

      // Reset
      formik.setFieldValue('firstname', '');
      formik.setFieldValue('lastname', '');
      formik.setFieldValue('company', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, email]);

  useEffect(() => {
    if (data && data.results.length === 1) {
      formik.setFieldValue(
        'firstname',
        data.results[0].first_name || '',
        false,
      );
      formik.setFieldValue('lastname', data.results[0].last_name || '', false);
      formik.setFieldValue('company', data.results[0].client.name || '', false);

      setTimeout(() => {
        formik.validateForm();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const initializeFormSchema = ({
    formId,
    formik: innerFormik,
  }: FormSchemaOptions): FormSchemaReturn<FormValues> => ({
    email: {
      formId,
      formik: innerFormik,
      name: 'email',
      renderField: () => (
        <Select
          name="wizer-email"
          label={intl.formatMessage({ id: 'sat.wizer.email' })}
          options={users.map((user) => ({
            label: user.email,
            value: user.email,
          }))}
          fullWidth
          onChange={(changedEmail) => {
            formik.setFieldValue('email', changedEmail);
            setEmail(changedEmail as string);
          }}
          value={email}
          sx={{ backgroundColor: 'white' }}
        />
      ),
    },
    firstname: {
      formId,
      formik: innerFormik,
      name: 'firstname',
      renderField: (props) => (
        <FieldText
          {...props}
          size="small"
          label={{ id: 'sat.wizer.firstname' }}
          sx={{ backgroundColor: 'white' }}
        />
      ),
    },
    lastname: {
      formId,
      formik: innerFormik,
      name: 'lastname',
      renderField: (props) => (
        <FieldText
          {...props}
          size="small"
          label={{ id: 'sat.wizer.lastname' }}
          sx={{ backgroundColor: 'white' }}
        />
      ),
    },
    company: {
      formId,
      formik: innerFormik,
      name: 'company',
      renderField: (props) => (
        <FieldText
          {...props}
          size="small"
          label={{ id: 'sat.wizer.company' }}
          sx={{ backgroundColor: 'white' }}
        />
      ),
    },
  });

  const formSchema = useMemo(
    () =>
      initializeFormSchema({
        formId: createFormId,
        formik,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik],
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <Form formId={createFormId} formik={formik}>
        <DialogTitle>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid
              container
              item
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item xs={10}>
                <Typography
                  variant="subtitleCapsL"
                  color="textSecondary"
                  textColor={palette.grey[400]}
                  label={{ id: 'sat.wizer.title' }}
                  noUserSelect
                />
              </Grid>
              <Grid item>
                <IconButton size="small" onClick={() => handleClose(true)}>
                  <CloseIcon style={{ color: palette.grey[400] }} />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ pt: '15px !important', pb: '0 !important' }}>
          <Grid container spacing={1} direction="column">
            <Stack gap={1.5}>
              <FormFieldControl {...formSchema.email} />
              <FormFieldControl {...formSchema.firstname} />
              <FormFieldControl {...formSchema.lastname} />
              <FormFieldControl {...formSchema.company} />
            </Stack>
            <Typography
              variant="caption"
              color="textSecondary"
              label={{ id: 'sat.wizer.create-note' }}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            label={{ id: 'global.cancel' }}
            onClick={() => handleClose(true)}
            mx={1}
          />
          <SubmitButton
            formId={createFormId}
            formik={formik}
            color="primary"
            label={{ id: 'sat.wizer.create' }}
            disabled={!formik.dirty || (formik.dirty && !formik.isValid)}
            mx={1}
            loading={formik.isSubmitting}
          />
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export interface WizerCreateAccountModalProps {
  activeClient: ControlClientInfoResponse;
  open: boolean;
  handleClose: (cancelled: boolean) => void;
}
